/**
 * HEIC/HEIF → JPEG conversion helpers.
 *
 * iPhones default to HEIC. Most browsers can't decode it and our upload
 * validators only accept JPEG/PNG/GIF/WebP, so we transparently convert
 * HEIC files in the browser before they hit validation.
 */

const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

const HEIC_EXTENSIONS = [".heic", ".heif"];

export const isHeic = (file: File): boolean => {
  if (file.type && HEIC_MIME_TYPES.has(file.type.toLowerCase())) return true;
  const name = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const swapExtension = (name: string, newExt: string): string => {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}${newExt}`;
};

/**
 * Memoized dynamic import of `heic-to`. Keeps the libheif wasm out of the
 * initial bundle and avoids re-fetching for every HEIC file in a batch.
 * On failure the cache is cleared so a later attempt (e.g. after the user
 * refreshes) can retry cleanly.
 */
let heicToPromise: Promise<typeof import("heic-to")> | null = null;

const loadHeicTo = (): Promise<typeof import("heic-to")> => {
  if (!heicToPromise) {
    heicToPromise = import("heic-to").catch((err) => {
      heicToPromise = null;
      throw err;
    });
  }
  return heicToPromise;
};

/**
 * Detects the specific "the JS chunk couldn't be fetched" error that Vite /
 * modern bundlers throw when an optimized-deps URL is stale after a
 * redeploy or HMR update. In that case there's no point retrying with the
 * PNG path — the module itself isn't loadable.
 */
export const isDynamicImportError = (err: unknown): boolean => {
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === "string"
        ? err
        : "";
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
};

export const convertHeicToJpeg = async (file: File): Promise<File> => {
  // heic-to wraps libheif 1.22.x, which handles modern iPhone HEIC
  // variants (HEVC 10-bit, `heix`/`mif1`) that heic2any's older build
  // could not decode.
  const { heicTo } = await loadHeicTo();

  const blob = await heicTo({
    blob: file,
    type: "image/jpeg",
    quality: 0.9,
  });

  return new File([blob], swapExtension(file.name, ".jpg"), {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
};

/**
 * PNG fallback path. libheif's PNG encoder occasionally succeeds on files
 * whose JPEG encode throws (odd color profiles, bit depths, etc.). Only
 * called when convertHeicToJpeg has already failed for the same file.
 */
export const convertHeicToPng = async (file: File): Promise<File> => {
  const { heicTo } = await loadHeicTo();

  const blob = await heicTo({
    blob: file,
    type: "image/png",
  });

  return new File([blob], swapExtension(file.name, ".png"), {
    type: "image/png",
    lastModified: file.lastModified,
  });
};


export interface HeicConversionResult {
  files: File[];
  errors: Array<{ name: string; error: unknown }>;
}

export type HeicProgressCallback = (completed: number, total: number) => void;

/**
 * Convert any HEIC files in the array to JPEG, sequentially (libheif is
 * memory-heavy — parallel conversion of 90 files OOMs mobile Safari).
 * Non-HEIC files are passed through untouched.
 */
export const convertHeicFiles = async (
  files: File[],
  onProgress?: HeicProgressCallback,
): Promise<HeicConversionResult> => {
  const heicIndexes = files
    .map((f, i) => (isHeic(f) ? i : -1))
    .filter((i) => i !== -1);

  if (heicIndexes.length === 0) {
    return { files, errors: [] };
  }

  const output = [...files];
  const errors: HeicConversionResult["errors"] = [];
  let done = 0;
  let moduleLoadFailure: unknown = null;

  for (const idx of heicIndexes) {
    if (moduleLoadFailure) {
      // The heic-to module itself failed to load earlier in this batch —
      // don't retry per file, just record the same failure for each
      // remaining HEIC so the UI can list them.
      console.warn("[HEIC] skipping — module load failed", files[idx].name);
      errors.push({ name: files[idx].name, error: moduleLoadFailure });
      output[idx] = null as unknown as File;
      done += 1;
      onProgress?.(done, heicIndexes.length);
      continue;
    }

    try {
      output[idx] = await convertHeicToJpeg(files[idx]);
    } catch (jpegErr) {
      if (isDynamicImportError(jpegErr)) {
        console.warn("[HEIC] dynamic import failed", files[idx].name, jpegErr);
        moduleLoadFailure = jpegErr;
        errors.push({ name: files[idx].name, error: jpegErr });
        output[idx] = null as unknown as File;
      } else {
        try {
          output[idx] = await convertHeicToPng(files[idx]);
        } catch (pngErr) {
          if (isDynamicImportError(pngErr)) {
            moduleLoadFailure = pngErr;
          }
          console.warn("[HEIC] conversion failed", files[idx].name, { jpegErr, pngErr });
          errors.push({ name: files[idx].name, error: pngErr });
          output[idx] = null as unknown as File;
        }
      }
    }
    done += 1;
    onProgress?.(done, heicIndexes.length);
  }


  return {
    files: output.filter((f): f is File => f != null),
    errors,
  };
};

export const hasAnyHeic = (files: File[]): boolean => files.some(isHeic);

/**
 * Format a friendly error message for HEIC conversion failures — truncates
 * long filename lists and appends an iPhone-settings hint.
 */
const describeError = (err: unknown, max = 200): string => {
  let msg: string;
  if (err instanceof Error) {
    msg = err.message || err.name || "Unknown error";
  } else if (typeof err === "string") {
    msg = err;
  } else {
    try {
      msg = JSON.stringify(err);
    } catch {
      msg = String(err);
    }
  }
  msg = msg.replace(/\s+/g, " ").trim();
  return msg.length > max ? `${msg.slice(0, max)}…` : msg;
};

export const formatHeicErrorMessage = (
  errors: HeicConversionResult["errors"],
  maxNames = 5,
): string => {
  // If every failure is the module-load error, the files themselves are
  // fine — surface a single actionable message instead of a per-file dump.
  if (errors.length > 0 && errors.every((e) => isDynamicImportError(e.error))) {
    return "Couldn't load the HEIC converter. This usually clears after a page refresh — please reload the page and try again.";
  }

  const shown = errors.slice(0, maxNames)
    .map((e) => `${e.name}: ${describeError(e.error)}`)
    .join(" | ");
  const more = errors.length > maxNames ? ` and ${errors.length - maxNames} more` : "";
  return `${shown}${more}. Tip: on iPhone, set Settings → Camera → Formats → Most Compatible to shoot JPEG going forward.`;
};
